import {Injectable, Injector} from '@angular/core';
import {LoginResponse, User, UserRole, UserStatus, USER_INIT} from "../models/user";
import {FMSFNotification} from "../models/notification";
import {Router} from "@angular/router";
import {SidebarService} from "./sidebar.service";
import {HttpService} from "./http.service";
import {HttpHeaders} from "@angular/common/http";
import {catchError, EMPTY} from "rxjs";
import {CaseDocketService} from "./case-docket.service";
import {Idle, DEFAULT_INTERRUPTSOURCES} from '@ng-idle/core';
import {Keepalive} from '@ng-idle/keepalive';

const USER_KEY = 'epds-user';
const REDIRECT_KEY = 'epds-redirect';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private _user : User = USER_INIT;
  private _bLoggedIn : boolean = false;
  private _redirectUrl : string = "";
  public editUserAccessRequestPending : FMSFNotification | undefined = undefined;
  private _dashType : string = "";
  private _loginResponse!: LoginResponse;

  // user search screen values
  search_email: any;
  search_firstname: any;
  search_lastname: any;
  search_Agency: any;
  // model run details search values
  mrd_search_user: any;
  mrd_search_model: any;
  mrd_search_status: any;
  mrd_search_createdStart: any;
  mrd_search_createdEnd: any;
  adv_search_bNo: any;
  adv_search_aNo: any;
  adv_search_solicitationNo: any;
  adv_search_companyName: any;
  adv_search_submissionFromDate: any;
  adv_search_submissionToDate: any;
  adv_search_dueFromDate: any;
  adv_search_dueToDate: any;
  adv_search_partyInfo: any;
  adv_search_intervenorCompName: any;
  adv_search_selectedAttorney: any;
  adv_search_selectedCaseStatus: any;
  adv_search_selectedLawGroup: any;
  adv_search_selectedCaseType: any;
  adv_search_t1Agency: any;
  adv_search_t2Agency: any;

  dashboard_filter = '';
  unassigned_dashboard_filter = '';
  assigned_dashboard_filter = '';

  idle_message = "";


  constructor(private router: Router,
              private sidebarService : SidebarService,
              private httpService: HttpService,
              private injector: Injector,
              private idle: Idle, private keepalive: Keepalive) {}

  get isLoggedIn(): boolean {
    return this._bLoggedIn;
  }

  set isLoggedIn(value: boolean) {
    this._bLoggedIn = value;
  }

  get userRole(): UserRole {
    return this._user.role_id;
  }

  set userRole(value: UserRole) {
    this._user.role_id = value;
  }

  get userStatus(): UserStatus {
    return this._user.account_status_id;
  }

  set userStatus(value: UserStatus) {
    this._user.account_status_id = value;
  }

  initUser() {
    if (this._user.user_Id == "") {
      const user = window.localStorage.getItem(USER_KEY);
      if (user) {
        this._user =  JSON.parse(user);
        this._bLoggedIn = true;
      }
    }
  }

  get user(): User {
    return this._user;
  }

  set user(user: User) {
    this._user = user;
    window.localStorage.removeItem(USER_KEY);
    window.localStorage.setItem(USER_KEY, JSON.stringify(user));
    this._bLoggedIn = true;
    this.idleReset();
  }

  isValidUser() : boolean {
    let ret = false;
    if (this._user.user_Id && this._user.email && this._user.account_status_id == UserStatus.Active) {
      ret = true;
    }
    return ret;
  }

  userLogout() {
    this.sidebarService.toggle(false);
    this.sidebarService.enabled = false;

    // have to use injector otherwise creates a circular dependency between state and case docket services
    const caseDocketService = this.injector.get(CaseDocketService);
    caseDocketService.clearDocket();

    window.localStorage.removeItem(USER_KEY);
    window.localStorage.removeItem(REDIRECT_KEY);
    this._bLoggedIn = false;
    this._user = USER_INIT;

    this.idle.stop();
    this.router.navigate(['/'] );
  }

  doLogout() {
    this.httpService.Post<User>('/logout', {}).subscribe(response => {
      // console.log(response);
      this.userLogout();
      this.checkLoginValid();
    });
  }

  get redirectUrl(): string {
    if (this._redirectUrl == "") {
      const redirectURL = window.localStorage.getItem(REDIRECT_KEY);
      if (redirectURL) {
        this._redirectUrl =  JSON.parse(redirectURL);
      }
    }
    return this._redirectUrl;
  }

  set redirectUrl(value: string) {
    this._redirectUrl = value;
    window.localStorage.removeItem(REDIRECT_KEY);
    window.localStorage.setItem(REDIRECT_KEY, JSON.stringify(value));
  }

  checkRedirect(elseUri? : string) {
    // if we have a normal user trying to access an admin url, clear out the redirect
    // Occurred because an admin clicked an admin link, then on login logged in as a normal user
    if (this.redirectUrl.length > 0 && this.redirectUrl.indexOf("/admin") > -1 && this.user.role_id != UserRole.GAO_Admin) {
      this.redirectUrl = "";
    }

    if (this.redirectUrl.length > 0) {
      this.router.navigate([this.redirectUrl] );
      this.redirectUrl = "";
    } else {
      if (elseUri) {
        this.router.navigate([elseUri] );
      }
    }
  }

  get dashType(): string {
    return this._dashType;
  }

  set dashType(value: string) {
    this._dashType = value;
  }


  get loginResponse(): LoginResponse {
    return this._loginResponse;
  }

  set loginResponse(value: LoginResponse) {
    this._loginResponse = value;
  }

  checkLoginValid() {
    this.checkSessionForUser();

    const headers = new HttpHeaders({'Content-Type':  'application/json'});
    const options = { ...headers};
    return  this.httpService.Get<User>('/', options).pipe(
      catchError(err => {
        // console.log('Handling error locally.', err);
        return EMPTY;
      })
    ).subscribe(response => {
      // console.log(response);
      if (!!response) {
        this.user = response;
        // this.util.init();
        this.checkRedirect();
      }
    });
  }

  checkSessionForUser() {
    // on a F5/reload, page will load faster than http call
    // check for user in session and use until call comes back.
    this.initUser();
  }

  idleReset() {
    // start watching and initialize variables
    this.idle.watch();
    this.idle_message = "";
  }

  configureIdleKeepalive() {
    this.idle.setIdle(61*20); // idle timeout before starting countdown
    this.idle.setTimeout(60*2); // idle countdown period
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // interrupts, things like clicks, scrolls, touches to the document

    this.idle.onIdleEnd.subscribe(() => this.idle_message = '');  // user did something
    this.idle.onTimeout.subscribe(() => {
      this.idle_message = "Your session has expired. Please login to continue."; // idle timeout, set logout message and log user out
      this.doLogout();
    });
    // user has gone idle, will probably ignore(not sub) and just use countdown
    // idle.onIdleStart.subscribe(() => this.stateService.idle_message = 'You\'ve gone idle!');
    this.idle.onTimeoutWarning.subscribe((countdown) => this.idle_message = "You are idle, you will be logged out in " + countdown + " seconds!");

    // keepalive ping interval
    this.keepalive.interval(304);
    this.keepalive.onPing.subscribe(() => {
      this.httpService.Get<any>('/refresh', {}).subscribe();
    });
  }
}
